import { API_URL, APP_VERSION } from '../config';
import { noteServerTime } from '../lib/serverTime';

/**
 * All backend calls go through here as POST + text/plain so the browser treats
 * them as CORS "simple requests" (no preflight — Apps Script cannot answer OPTIONS).
 * Apps Script runs doPost on the first hop and 302-redirects to the computed JSON,
 * with Access-Control-Allow-Origin: * on the response. The session token travels
 * in the body, never in a header.
 *
 * Apps Script cold-starts (2-5s) and a redirect hop make single requests slow, so:
 *  - 30s timeout (a cold start + a big query can take >20s)
 *  - one automatic retry on a network/timeout/5xx/garbled response; every action
 *    is safe to retry (reads are idempotent; submitReading dedupes on submissionId)
 */
const TIMEOUT_MS = 30_000;
const RETRY_DELAY_MS = 1200;

export async function postAction<T>(
  action: string,
  payload: Record<string, unknown> = {},
  opts: { timeoutMs?: number; retries?: number } = {},
): Promise<T> {
  if (!API_URL) throw new ApiError('CONFIG', 'App is not configured (missing API URL).');
  const body = JSON.stringify({ action, appVersion: APP_VERSION, ...payload });
  const timeoutMs = opts.timeoutMs ?? TIMEOUT_MS;
  const maxAttempts = (opts.retries ?? 2) + 1;

  let lastErr: ApiError = new ApiError('NETWORK', 'Could not reach the server.');
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await once<T>(body, timeoutMs);
    } catch (e) {
      lastErr = e instanceof ApiError ? e : new ApiError('NETWORK', 'Could not reach the server.', e);
      if (attempt < maxAttempts && isRetryable(lastErr)) {
        await sleep(RETRY_DELAY_MS);
        continue;
      }
      throw lastErr;
    }
  }
  throw lastErr;
}

async function once<T>(body: string, timeoutMs: number): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body,
      redirect: 'follow',
      signal: ctrl.signal,
    });
  } catch (e) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    throw new ApiError(
      offline ? 'OFFLINE' : 'NETWORK',
      offline ? 'You are offline.' : 'The server did not respond in time.',
      e,
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    throw new ApiError('HTTP_' + res.status, 'The server did not respond correctly. Retrying may help.');
  }

  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    // Apps Script occasionally serves an HTML interstitial under load
    throw new ApiError('BAD_RESPONSE', 'The server returned an unexpected response. Retrying may help.');
  }
  const obj = data as { now?: string };
  if (obj && typeof obj.now === 'string') noteServerTime(obj.now);
  return data as T;
}

function isRetryable(e: ApiError): boolean {
  // Apps Script has no real 404/403 — those are transient Google front-end hiccups.
  return e.code === 'NETWORK' || e.code === 'BAD_RESPONSE' || e.code === 'HTTP_429' ||
    e.code === 'HTTP_404' || e.code === 'HTTP_403' || e.code.startsWith('HTTP_5');
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class ApiError extends Error {
  code: string;
  cause?: unknown;
  constructor(code: string, message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.cause = cause;
  }
}

export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** True when an error means "couldn't reach the server" (offline OR slow/busy). */
export function isConnectivityError(e: unknown): boolean {
  return e instanceof ApiError &&
    (e.code === 'NETWORK' || e.code === 'OFFLINE' || e.code === 'BAD_RESPONSE' ||
      e.code === 'HTTP_429' || e.code.startsWith('HTTP_5'));
}
